import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, CheckCircle2, PauseCircle, PlusCircle, Users } from "lucide-react";
import {
  createTenant,
  createTenantAdmin,
  fetchPlatformDashboard,
  fetchPlatformTenants,
  updateTenant,
  type PlatformTenant,
} from "../api/platform";
import { ApiError } from "../api/client";
import { Button, Card, EmptyState, ErrorState, Field, PageHeader, Spinner } from "../components/ui";
import { StatusPill } from "../components/status";
import { formatDateShort } from "../lib/format";

function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="stat-card">
      <div className="v">{typeof value === "number" ? value.toLocaleString("id-ID") : value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export function PlatformDashboardPage() {
  const dashQuery = useQuery({ queryKey: ["platform", "dashboard"], queryFn: fetchPlatformDashboard });
  if (dashQuery.isPending) return <Spinner />;
  if (dashQuery.isError || !dashQuery.data) {
    return <ErrorState onRetry={() => void dashQuery.refetch()} />;
  }
  const d = dashQuery.data;
  return (
    <div>
      <PageHeader title="Dashboard Platform" subtitle="Ringkasan seluruh platform." />
      <div className="stat-grid" style={{ marginBottom: 16 }}>
        <Stat value={d.totalTenant} label="Total Lokasi" />
        <Stat value={d.activeTenant} label="Lokasi Aktif" />
        <Stat value={d.totalBooking} label="Total Booking" />
        <Stat value={d.totalPatient} label="Total Pasien" />
        <Stat value={d.totalTherapist} label="Total Terapis" />
      </div>
      <Card>
        <p className="muted-line" style={{ margin: 0 }}>
          <Building2 size={14} /> Kelola lokasi melalui menu Tenants.
        </p>
      </Card>
    </div>
  );
}

export function PlatformTenantsPage() {
  const queryClient = useQueryClient();
  const listQuery = useQuery({ queryKey: ["platform", "tenants"], queryFn: fetchPlatformTenants });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", email: "", phone: "", address: "", description: "" });
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adminPw, setAdminPw] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });

  async function create() {
    setBusy(true);
    setMsg(null);
    try {
      await createTenant({
        name: form.name,
        slug: form.slug,
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        description: form.description || undefined,
      });
      setShowCreate(false);
      setForm({ name: "", slug: "", email: "", phone: "", address: "", description: "" });
      await invalidate();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Gagal membuat lokasi.");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(t: PlatformTenant) {
    setMsg(null);
    try {
      await updateTenant(t.id, { status: t.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE" });
      await invalidate();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Gagal mengubah status.");
    }
  }

  async function createAdmin(t: PlatformTenant) {
    const fullName = window.prompt(`Nama admin untuk ${t.name}:`);
    if (!fullName) return;
    const email = window.prompt("Email admin:");
    if (!email) return;
    try {
      const res = await createTenantAdmin(t.id, { fullName, email });
      if (res.initialPassword) {
        setAdminPw(`${res.admin.email} → ${res.initialPassword}`);
      }
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Gagal membuat admin.");
    }
  }

  return (
    <div>
      <PageHeader
        title="Tenants"
        subtitle="Lokasi penyelenggara layanan di platform."
        action={<Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? "Tutup" : "+ Lokasi"}</Button>}
      />
      {msg ? <p className="alert alert-error">{msg}</p> : null}
      {adminPw ? (
        <p className="alert" style={{ background: "var(--teal-50)", color: "var(--teal-700)" }}>
          Akun admin dibuat. Kata sandi awal: <strong>{adminPw}</strong>
        </p>
      ) : null}

      {showCreate ? (
        <Card className="form-stack" style={{ marginBottom: 14 }}>
          <Field label="Nama lokasi" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field
            label="Slug (URL pendek, huruf kecil + tanda hubung)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="totok-melati"
          />
          <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field label="Telepon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field label="Alamat" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <Field label="Deskripsi" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Button onClick={() => void create()} loading={busy}>
            Buat Lokasi
          </Button>
        </Card>
      ) : null}

      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Belum ada lokasi." hint="Buat lokasi pertama." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {listQuery.data?.map((t: PlatformTenant) => (
            <Card key={t.id}>
              <div className="list-row-top">
                <div>
                  <strong style={{ fontSize: 16 }}>{t.name}</strong>
                  <p className="muted" style={{ margin: "2px 0 0" }}>
                    /{t.slug} · {t.address ?? "-"}
                  </p>
                </div>
                <StatusPill kind="booking" value={t.status === "ACTIVE" ? "CONFIRMED" : "CANCELLED"} />
              </div>
              <div style={{ display: "flex", gap: 16, margin: "10px 0", flexWrap: "wrap" }}>
                <span className="muted-line">
                  <Users size={12} /> {t._count.patientProfiles} pasien
                </span>
                <span className="muted-line">
                  <Building2 size={12} /> {t._count.therapists} terapis · {t._count.bookings} booking
                </span>
                <span className="muted-line">Dibuat {formatDateShort(t.createdAt.slice(0, 10))}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="outline" onClick={() => void createAdmin(t)}>
                  + Admin Lokasi
                </Button>
                <Button
                  variant={t.status === "ACTIVE" ? "danger" : "primary"}
                  onClick={() => void toggle(t)}
                >
                  {t.status === "ACTIVE" ? (
                    <>
                      <PauseCircle size={16} /> Nonaktifkan
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} /> Aktifkan
                    </>
                  )}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export { PlusCircle };
